import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../layouts/AppLayout.css';

interface NewsItem {
    id: number;
    doc_type: string;
    icon: string;
    tag: string;
    tag_ar: string;
    title: string;
    title_ar: string;
    desc: string;
    desc_ar?: string;
    date: string;
}

const NewsCarousel: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLatestNews = async () => {
            try {
                const response = await api.get(`/latest-news?lang=${i18n.language}`);
                if (response.data?.items) {
                    setNewsItems(response.data.items);
                }
            } catch (error) {
                console.error('Error fetching latest news:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchLatestNews();
    }, [i18n.language]);

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, clientWidth } = scrollContainerRef.current;
            const totalScroll = scrollContainerRef.current.scrollWidth - clientWidth;
            const progress = totalScroll > 0 ? scrollLeft / totalScroll : 0;
            const maxIndex = Math.ceil(newsItems.length / 3) - 1;
            setActiveIndex(Math.min(Math.round(progress * maxIndex), maxIndex));
        }
    };

    const scrollToPage = (index: number) => {
        if (scrollContainerRef.current) {
            const width = scrollContainerRef.current.clientWidth;
            scrollContainerRef.current.scrollTo({
                left: index * width,
                behavior: 'smooth'
            });
            setActiveIndex(index);
        }
    };

    const handleCardClick = (item: NewsItem) => {
        const routes: Record<string, string> = {
            jurisprudence: 'detail/jurisprudence',
            code_article: 'detail/code-article',
            joarticle: 'detail/jo',
            these: 'detail/theses'
        };
        const route = routes[item.doc_type];
        if (route) {
            navigate(`/${i18n.language}/${route}/${item.id}`);
        }
    };

    const getBadgeClass = (docType: string) => {
        const classes: Record<string, string> = {
            jurisprudence: 'badge-jps',
            code_article: 'badge-code',
            joarticle: 'badge-jo',
            these: 'badge-thesis'
        };
        return classes[docType] || 'badge-secondary';
    };

    const pageCount = Math.max(1, Math.ceil(newsItems.length / 3));

    if (loading) {
        return (
            <div className="news-carousel-wrapper">
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (newsItems.length === 0) {
        return null;
    }

    return (
        <div className="news-carousel-wrapper">
            <div
                className="news-carousel-track"
                ref={scrollContainerRef}
                onScroll={handleScroll}
            >
                {newsItems.map((item) => (
                    <div
                        key={`${item.doc_type}-${item.id}`}
                        className="news-card carousel-item-card"
                        onClick={() => handleCardClick(item)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className={`news-tag premium-badge ${getBadgeClass(item.doc_type)}`}>
                            <i className={`bi ${item.icon} me-1`}></i>
                            {i18n.language === 'ar' ? item.tag_ar : item.tag}
                        </div>
                        <h3>{i18n.language === 'ar' ? item.title_ar : item.title}</h3>
                        <p>{i18n.language === 'ar' ? (item.desc_ar || item.desc) : item.desc}</p>
                        {item.date && <span className="news-date">{item.date}</span>}
                    </div>
                ))}
            </div>

            {pageCount > 1 && (
                <div className="carousel-dots">
                    {Array.from({ length: pageCount }).map((_, idx) => (
                        <button
                            key={idx}
                            className={`carousel-dot ${idx === activeIndex ? 'active' : ''}`}
                            onClick={() => scrollToPage(idx)}
                            aria-label={`Page ${idx + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default NewsCarousel;
